/**
 * PATH: apps/billing/src/pages/DashboardPage.tsx
 * FILE_ID: BILLING-PAGE-DASHBOARD-001
 * PURPOSE: KPI dashboard for the active tenant's billing pipeline.
 * WHY: Was a static mock. Now renders the live shape of /api/bills/stats/.
 *
 * FLOW:
 *   useBillStats() -> /api/bills/stats/ -> {counts, amounts, processing, recent}
 *     -> 4 KPI tiles + recent list + processing breakdown
 *
 * DEPENDENCIES:
 *   ../hooks/useBills: useBillStats query hook
 */

import { Link } from 'react-router-dom';
import {
  FileText,
  Clock,
  CheckCircle,
  AlertTriangle,
  TrendingUp,
  DollarSign,
  Inbox,
  ArrowUpRight,
  Loader2,
  RefreshCcw,
} from 'lucide-react';
import { cn, formatCurrency } from '@/lib/utils';
import {
  useBillStats,
  type BillListRow,
  type BillStatus,
} from '../hooks/useBills';

// ---------------------------------------------------------------------------
// Status -> badge colour map (mirrors BillsPage so visual language matches)
// ---------------------------------------------------------------------------

const statusBadge: Record<BillStatus, string> = {
  pending: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
  extracted: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200',
  reviewed: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
  pushed: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
  failed: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200',
  quarantined: 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200',
  awaiting_document: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
  needs_fetch: 'bg-slate-100 text-slate-800 dark:bg-slate-800/40 dark:text-slate-200',
  needs_review: 'bg-fuchsia-100 text-fuchsia-800 dark:bg-fuchsia-900/40 dark:text-fuchsia-200',
};

const statusLabel: Record<BillStatus, string> = {
  pending: 'Pending',
  extracted: 'AI extracted',
  reviewed: 'Reviewed',
  pushed: 'Pushed',
  failed: 'Failed',
  quarantined: 'Quarantined',
  awaiting_document: 'Awaiting document',
  needs_fetch: 'Needs fetch',
  needs_review: 'Needs review',
};

function parseAmount(v: string | null | undefined): number | null {
  if (v == null) return null;
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : null;
}

function relativeTime(iso: string | null | undefined): string {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms)) return '';
  const s = Math.round(ms / 1000);
  if (s < 60) return 'just now';
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min${m === 1 ? '' : 's'} ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} hr${h === 1 ? '' : 's'} ago`;
  const d = Math.round(h / 24);
  return `${d} day${d === 1 ? '' : 's'} ago`;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function DashboardPage() {
  // Poll every 30 seconds — bills come in quickly during business hours;
  // matching the 30s we use for AR/AP rollup in email_kanban.
  const { data, isLoading, error, refetch, isFetching } = useBillStats({ pollMs: 30_000 });

  // Pre-render values; safe defaults so the layout doesn't shift in/out.
  const total = data?.counts.total ?? 0;
  const pending = data?.counts.by_status?.pending ?? 0;
  const reviewed = data?.counts.by_status?.reviewed ?? 0;
  const failed = data?.counts.by_status?.failed ?? 0;
  const pushed24h = data?.processing?.pushed_24h ?? 0;
  const aiPct = data?.processing?.ai_extracted_pct ?? 0;
  const autoPushPct = data?.processing?.auto_pushed_pct ?? 0;
  const approvalPct = data?.processing?.approval_pct ?? 0;

  const tiles = [
    {
      name: 'Pending bills',
      value: pending,
      sub: `${total} total`,
      icon: FileText,
      color: 'bg-blue-500',
    },
    {
      name: 'Awaiting approval',
      value: reviewed,
      sub: reviewed > 0 ? 'Review queue' : 'All clear',
      icon: Clock,
      color: 'bg-amber-500',
    },
    {
      name: 'Pushed (24h)',
      value: pushed24h,
      sub: 'To Xero',
      icon: CheckCircle,
      color: 'bg-emerald-500',
    },
    {
      name: 'Failed',
      value: failed,
      sub: failed > 0 ? 'Needs attention' : 'None',
      icon: AlertTriangle,
      color: failed > 0 ? 'bg-red-500' : 'bg-gray-400',
    },
  ];

  const recent: BillListRow[] = data?.recent ?? [];

  return (
    <div className="p-6 space-y-8">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Dashboard</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Overview of your billing automation
          </p>
        </div>
        <button
          onClick={() => refetch()}
          disabled={isFetching}
          className="flex items-center gap-2 px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
          aria-label="Refresh dashboard"
        >
          <RefreshCcw className={cn('h-3.5 w-3.5', isFetching && 'animate-spin')} />
          Refresh
        </button>
      </div>

      {error && (
        <div className="rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-4 text-sm text-red-700 dark:text-red-300 flex items-center gap-2">
          <AlertTriangle className="h-4 w-4" />
          Couldn't load stats: {error.message}
          <button onClick={() => refetch()} className="ml-auto underline">
            Retry
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {tiles.map((tile) => (
          <div
            key={tile.name}
            className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">{tile.name}</p>
                <p className="text-3xl font-bold text-gray-900 dark:text-white mt-1">
                  {isLoading ? <Loader2 className="h-7 w-7 animate-spin" /> : tile.value}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{tile.sub}</p>
              </div>
              <div
                className={cn(
                  'h-12 w-12 rounded-lg flex items-center justify-center',
                  tile.color,
                )}
              >
                <tile.icon className="h-6 w-6 text-white" />
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent bills (left column, 2/3 width) */}
        <div className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
          <div className="p-6 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Recent bills</h2>
            <Link
              to="/bills"
              className="text-sm text-emerald-600 hover:text-emerald-700 flex items-center gap-1"
            >
              View all <ArrowUpRight className="h-3 w-3" />
            </Link>
          </div>
          <div className="p-6 space-y-4">
            {isLoading ? (
              <div className="flex items-center justify-center py-8 text-gray-500">
                <Loader2 className="h-5 w-5 animate-spin mr-2" />
                Loading…
              </div>
            ) : recent.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-6">
                No bills yet. Forward a supplier invoice to your Outlook inbox or upload one.
              </p>
            ) : (
              recent.map((bill) => {
                const amount = parseAmount(bill.total);
                return (
                  <Link
                    key={bill.id}
                    to={`/bills/${bill.id}`}
                    className="flex items-center justify-between py-3 border-b border-gray-100 dark:border-gray-700 last:border-0 hover:bg-gray-50 dark:hover:bg-gray-700 -mx-2 px-2 rounded"
                  >
                    <div className="flex items-center gap-4">
                      <div className="h-10 w-10 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                        <FileText className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">
                          {bill.vendor_name || bill.email_from || '(unknown vendor)'}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {bill.invoice_number ? `#${bill.invoice_number} · ` : ''}
                          {relativeTime(bill.updated_at || bill.created_at)}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <span
                        className={cn(
                          'px-2 py-1 rounded-full text-xs font-medium',
                          statusBadge[bill.status],
                        )}
                      >
                        {statusLabel[bill.status]}
                      </span>
                      <span className="font-semibold text-gray-900 dark:text-white w-24 text-right">
                        {amount != null ? formatCurrency(amount) : '—'}
                      </span>
                    </div>
                  </Link>
                );
              })
            )}
          </div>
        </div>

        {/* Sidebar (right column, 1/3 width) */}
        <div className="space-y-6">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
            <div className="p-6 border-b border-gray-100 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Quick actions</h2>
            </div>
            <div className="p-4 space-y-3">
              <Link
                to="/inbox"
                className="w-full flex items-center gap-3 p-3 rounded-lg bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors text-left"
              >
                <Inbox className="h-5 w-5 text-emerald-600" />
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">Sync emails</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Check for new bills</p>
                </div>
              </Link>
              <Link
                to="/bills?status=reviewed"
                className="w-full flex items-center gap-3 p-3 rounded-lg bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors text-left"
              >
                <DollarSign className="h-5 w-5 text-blue-600" />
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">Push to Xero</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Sync approved bills</p>
                </div>
              </Link>
              <Link
                to="/bills"
                className="w-full flex items-center gap-3 p-3 rounded-lg bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors text-left"
              >
                <TrendingUp className="h-5 w-5 text-purple-600" />
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">View bills</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">All pipeline rows</p>
                </div>
              </Link>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
            <div className="p-6 border-b border-gray-100 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Processing stats
              </h2>
            </div>
            <div className="p-6 space-y-4">
              <ProgressRow label="AI auto-extracted" pct={aiPct} barClass="bg-emerald-500" />
              <ProgressRow label="Auto-pushed to Xero" pct={autoPushPct} barClass="bg-blue-500" />
              <ProgressRow label="Approved on first review" pct={approvalPct} barClass="bg-amber-500" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ProgressRow({
  label,
  pct,
  barClass,
}: {
  label: string;
  pct: number;
  barClass: string;
}) {
  // Server returns 0-100. Clamp defensively in case the contract drifts.
  const safePct = Math.max(0, Math.min(100, Math.round(pct)));
  return (
    <div>
      <div className="flex justify-between text-sm mb-1">
        <span className="text-gray-600 dark:text-gray-400">{label}</span>
        <span className="font-medium text-gray-900 dark:text-white">{safePct}%</span>
      </div>
      <div className="h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
        <div className={cn('h-full rounded-full transition-all', barClass)} style={{ width: `${safePct}%` }} />
      </div>
    </div>
  );
}
